import type { LayerProps } from "react-map-gl/mapbox";

import type { MvtLayerConfig } from "@/components/base/map/MapBoxComponent.types";
import {
	createMvtClusterCirclePaint,
	createMvtClusterCountLayout,
	createMvtClusterCountPaint,
	createMvtFillPaint,
	createMvtLinePaint,
	createMvtUnclusteredPointPaint,
	createMvtVertexPaint,
	MAP_CLUSTER_COLORS,
	MVT_LAYER_RENDER_ORDER,
} from "@/components/base/map/map-cluster-styles";
import {
	MAPBOX_LINE_OR_POLYGON_GEOMETRY_FILTER,
	MAPBOX_POINT_GEOMETRY_FILTER,
	MAPBOX_POLYGON_GEOMETRY_FILTER,
} from "@/features/map/lib/mapbox-geometry-filters";
import type { MapEntityKind } from "@/features/map/types";
import { mapService } from "@/services/map/map.service";
import {
	combineMapboxFilter,
	MAPBOX_MULTI_POINT_CLUSTER_FILTER,
	MAPBOX_UNCLUSTERED_POINT_FILTER,
} from "@/utils/map/map-cluster-filters";

/** Stable Mapbox source/layer ids for server MVT entity layers. */
export const ServerMvtLayerId = {
	Jobs: "jobs-mvt",
	Tickets: "tickets-mvt",
	Assets: "assets-mvt",
	Records: "records-mvt",
} as const;

export type ServerMvtLayerId =
	(typeof ServerMvtLayerId)[keyof typeof ServerMvtLayerId];

/** Mapbox layer role id for always-on line/polygon vertex circles. */
export const MVT_VERTICES_LAYER_ID = "mvt-vertices";

/**
 * True when a rendered Mapbox layer id is an MVT vertex circle layer
 * (`{layerId}-mvt-vertices`, e.g. `assets-mvt-mvt-vertices`).
 */
export function isMvtVertexLayerId(
	layerId: string | undefined | null,
): boolean {
	return layerId?.endsWith(`-${MVT_VERTICES_LAYER_ID}`) === true;
}

/**
 * Client-only tile URL query key so Mapbox partitions its tile cache by
 * sub-org filter view. The API ignores this param (same pattern as `_org`).
 */
export const MVT_TILE_VIEW_CACHE_QUERY_KEY = "_view";
export const MVT_TILE_LIVE_EPOCH_QUERY_KEY = "_live";

/** Swagger tile scoping query params (`forJobId`, `forTicketId`, …). */
export interface ServerMvtTileScope {
	forJobId?: string;
	forTicketId?: string;
	forAssetId?: string;
	forRecordId?: string;
}

interface ServerMvtLayerDefinition {
	id: ServerMvtLayerId;
	sourceLayer: "Jobs" | "Tickets" | "Assets" | "Records";
	color: string;
	defaultLabel: string;
	getUrlTemplate: (organizationId: string) => string;
}

const LAYER_DEFINITIONS: Record<ServerMvtLayerId, ServerMvtLayerDefinition> = {
	[ServerMvtLayerId.Jobs]: {
		id: ServerMvtLayerId.Jobs,
		sourceLayer: "Jobs",
		color: MAP_CLUSTER_COLORS.jobs,
		defaultLabel: "Jobs",
		getUrlTemplate: (organizationId) =>
			mapService.getJobsTilesUrlTemplate(organizationId),
	},
	[ServerMvtLayerId.Tickets]: {
		id: ServerMvtLayerId.Tickets,
		sourceLayer: "Tickets",
		color: MAP_CLUSTER_COLORS.tickets,
		defaultLabel: "Tickets",
		getUrlTemplate: (organizationId) =>
			mapService.getTicketsTilesUrlTemplate(organizationId),
	},
	[ServerMvtLayerId.Assets]: {
		id: ServerMvtLayerId.Assets,
		sourceLayer: "Assets",
		color: MAP_CLUSTER_COLORS.assets,
		defaultLabel: "Assets",
		getUrlTemplate: (organizationId) =>
			mapService.getAssetsTilesUrlTemplate(organizationId),
	},
	[ServerMvtLayerId.Records]: {
		id: ServerMvtLayerId.Records,
		sourceLayer: "Records",
		color: MAP_CLUSTER_COLORS.records,
		defaultLabel: "Records",
		getUrlTemplate: (organizationId) =>
			mapService.getRecordsTilesUrlTemplate(organizationId),
	},
};

/** Optional sub-org (or other) Mapbox filters split by layer role. */
export interface ServerMvtExtraFilters {
	/** AND-combined into `clusters` / `cluster-count` layers. */
	cluster?: unknown[];
	/** AND-combined into unclustered fill/line/point layers. */
	feature?: unknown[];
}

export interface BuildServerMvtLayersArgs {
	organizationId: string;
	/** Which entity MVT sources to include. */
	layerIds: readonly ServerMvtLayerId[];
	/** Optional related-entity filters from swagger tile APIs. */
	scope?: ServerMvtTileScope;
	/**
	 * Optional static `clusterServerSide` query on the tile URL template.
	 * When omitted (default), zoom gating in `mapService.getTransformRequest`
	 * sets the param per tile `z` via `MVT_SERVER_CLUSTER_MAX_ZOOM`.
	 */
	clusterServerSide?: boolean;
	/** Optional label overrides keyed by layer id. */
	labels?: Partial<Record<ServerMvtLayerId, string>>;
	/**
	 * Optional extra Mapbox filters (e.g. sub-org), AND-combined separately
	 * into cluster vs unclustered geometry layers.
	 */
	extraFilters?: ServerMvtExtraFilters;
	/**
	 * Client-only Mapbox tile-cache partition (`_view=…`).
	 * Use {@link resolveMvtTileViewCacheKey} (`all` / id / `multi`).
	 */
	viewCacheKey?: string;
	/** Client-only cache partition incremented by live entity events. */
	liveEpoch?: number;
}

/**
 * Appends query params to an MVT tile URL template, preserving existing query keys.
 * Skips `null`, `undefined`, and empty-string values.
 */
export function appendMvtTileQuery(
	url: string,
	params: Record<string, string | boolean | null | undefined>,
): string {
	const entries = Object.entries(params).filter(
		(entry): entry is [string, string | boolean] =>
			entry[1] != null && entry[1] !== "",
	);
	if (entries.length === 0) return url;

	let result = url;
	for (const [key, value] of entries) {
		const sep = result.includes("?") ? "&" : "?";
		result = `${result}${sep}${encodeURIComponent(key)}=${encodeURIComponent(String(value))}`;
	}
	return result;
}

/**
 * Resolves the client-only `_view` tile-cache key from UI / host selection.
 *
 * @returns `"all"` for org-level, a single sub-org id, or `"multi"`.
 */
export function resolveMvtTileViewCacheKey(
	effectiveSubOrgIds: readonly string[],
): string {
	if (effectiveSubOrgIds.length === 0) return "all";
	if (effectiveSubOrgIds.length === 1) {
		return effectiveSubOrgIds[0] as string;
	}
	return "multi";
}

/**
 * Mapbox vector source id for an MVT layer, optionally partitioned by view cache key.
 */
export function getMvtVectorSourceId(
	layerId: string,
	cacheKey?: string,
): string {
	if (cacheKey != null && cacheKey.length > 0) {
		return `${layerId}-mvt-${cacheKey}`;
	}
	return `${layerId}-mvt`;
}

/**
 * Extracts the MVT layer config id from a Mapbox source id produced by
 * {@link getMvtVectorSourceId} (`jobs-mvt-mvt` or `jobs-mvt-mvt-all`).
 */
export function parseMvtLayerIdFromSourceId(sourceId: string): string | null {
	const match = /^(.*)-mvt(?:-(.+))?$/.exec(sourceId);
	return match?.[1] ?? null;
}

/**
 * MVT layer ids to show as related/context layers for a workspace host entity.
 */
export function workspaceMvtLayerIds(kind: MapEntityKind): ServerMvtLayerId[] {
	switch (kind) {
		case "job":
			return [ServerMvtLayerId.Assets, ServerMvtLayerId.Records];
		case "ticket":
			return [ServerMvtLayerId.Assets, ServerMvtLayerId.Records];
		case "asset":
			return [
				ServerMvtLayerId.Jobs,
				ServerMvtLayerId.Tickets,
				ServerMvtLayerId.Records,
			];
		case "record":
			return [
				ServerMvtLayerId.Jobs,
				ServerMvtLayerId.Tickets,
				ServerMvtLayerId.Assets,
			];
	}
}

/**
 * Tile scope query for the active workspace entity.
 */
export function workspaceMvtTileScope(
	kind: MapEntityKind,
	entityId: string,
): ServerMvtTileScope {
	switch (kind) {
		case "job":
			return { forJobId: entityId };
		case "ticket":
			return { forTicketId: entityId };
		case "asset":
			return { forAssetId: entityId };
		case "record":
			return { forRecordId: entityId };
	}
}

function buildGeometryLayers(
	def: ServerMvtLayerDefinition,
	extraFilters: ServerMvtExtraFilters | undefined,
): LayerProps[] {
	const clusterFilter = combineMapboxFilter(
		[...MAPBOX_MULTI_POINT_CLUSTER_FILTER],
		extraFilters?.cluster,
	);
	const unclusteredFilter = combineMapboxFilter(
		[...MAPBOX_UNCLUSTERED_POINT_FILTER],
		extraFilters?.feature,
	);
	const unclusteredPointFilter = combineMapboxFilter(unclusteredFilter, [
		...MAPBOX_POINT_GEOMETRY_FILTER,
	]);
	const unclusteredPolygonFilter = combineMapboxFilter(unclusteredFilter, [
		...MAPBOX_POLYGON_GEOMETRY_FILTER,
	]);
	const unclusteredLineFilter = combineMapboxFilter(unclusteredFilter, [
		...MAPBOX_LINE_OR_POLYGON_GEOMETRY_FILTER,
	]);

	return [
		{
			id: "mvt-fill",
			type: "fill",
			"source-layer": def.sourceLayer,
			filter: unclusteredPolygonFilter as LayerProps["filter"],
			paint: createMvtFillPaint(def.color),
		},
		{
			id: "mvt-line",
			type: "line",
			"source-layer": def.sourceLayer,
			filter: unclusteredLineFilter as LayerProps["filter"],
			paint: createMvtLinePaint(def.color),
		},
		{
			id: MVT_VERTICES_LAYER_ID,
			type: "circle",
			"source-layer": def.sourceLayer,
			filter: unclusteredLineFilter as LayerProps["filter"],
			paint: createMvtVertexPaint(def.color),
		},
		{
			id: "mvt-points",
			type: "circle",
			"source-layer": def.sourceLayer,
			filter: unclusteredPointFilter as LayerProps["filter"],
			paint: createMvtUnclusteredPointPaint(def.color),
		},
		{
			id: "clusters",
			type: "circle",
			"source-layer": def.sourceLayer,
			filter: clusterFilter as LayerProps["filter"],
			paint: createMvtClusterCirclePaint(def.color),
		},
		{
			id: "cluster-count",
			type: "symbol",
			"source-layer": def.sourceLayer,
			filter: clusterFilter as LayerProps["filter"],
			layout: createMvtClusterCountLayout(),
			paint: createMvtClusterCountPaint(),
		},
	] as LayerProps[];
}

/**
 * Builds Mapbox `MvtLayerConfig` entries for server-side clustered tile sources.
 */
export function buildServerMvtLayers({
	organizationId,
	layerIds,
	scope,
	clusterServerSide,
	labels,
	extraFilters,
	viewCacheKey,
	liveEpoch,
}: BuildServerMvtLayersArgs): MvtLayerConfig[] {
	const selected = new Set(layerIds);
	const orderedIds = MVT_LAYER_RENDER_ORDER.filter((id) =>
		selected.has(id),
	) as ServerMvtLayerId[];
	const cacheKey =
		viewCacheKey != null && viewCacheKey.length > 0 ? viewCacheKey : undefined;

	return orderedIds.map((id) => {
		const def = LAYER_DEFINITIONS[id];
		const baseUrl = def.getUrlTemplate(organizationId);
		const url = appendMvtTileQuery(baseUrl, {
			clusterServerSide,
			forJobId: scope?.forJobId,
			forTicketId: scope?.forTicketId,
			forAssetId: scope?.forAssetId,
			forRecordId: scope?.forRecordId,
			...(cacheKey != null
				? { [MVT_TILE_VIEW_CACHE_QUERY_KEY]: cacheKey }
				: {}),
			[MVT_TILE_LIVE_EPOCH_QUERY_KEY]: String(liveEpoch ?? 0),
		});

		return {
			id: def.id,
			url,
			sourceLayer: def.sourceLayer,
			color: def.color,
			label: labels?.[id] ?? def.defaultLabel,
			cacheKey,
			layers: buildGeometryLayers(def, extraFilters),
		};
	});
}
